import { Link } from 'react-router-dom';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppBar } from '@mui/material';
import { Box } from '@mui/material/';
import { Toolbar } from '@mui/material/';
import { IconButton } from '@mui/material/';
import { Typography } from '@mui/material/';
import { Container } from '@mui/material/';
import { Avatar } from '@mui/material/';
import MenuIcon from '@mui/icons-material/Menu';

import Banner from './Banner';
import CreateProjectPartial from './Partials/ProjectsAdd';
import { appContext } from '../Contexts/AppContext';
import { SwitchCompanyDialog } from './Dialogs/SwitchCompanyDialog';
import { UserMenu } from './UserMenu';

interface NavbarProps {
  signOut: () => void;
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const Navbar = ({ signOut, setIsDrawerOpen }: NavbarProps) => {
  const { user } = useContext(appContext);
  const { t } = useTranslation();
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] =
    useState(false);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { company } = useContext(appContext);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    // When click OUTSIDE of the user menu
    setAnchorElUser(null);
  };

  const handleSidebar = () => {
    setIsDrawerOpen((prevState) => !prevState);
  };

  const closeCreateProjectModal = () => {
    setIsCreateProjectModalOpen(false);
  };

  const handleOpenModal = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    handleCloseUserMenu();
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const AppLogo = () => (
    <Link
      to={`/${company}`}
      style={{
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <img
        src="/als.png"
        alt="ALS"
        loading="lazy"
        height="40"
        style={{ verticalAlign: 'middle' }}
      />
      <Typography
        component="h1"
        variant="h6"
        sx={{
          display: { xs: 'none', sm: 'inline-block' },
          color: 'white',
        }}
      >
        {t('HEADING_APP_NAME')}
      </Typography>
    </Link>
  );

  const HamburgerButton = () => (
    <IconButton
      size="large"
      aria-label={t('LABEL_OPEN_SIDEBAR')}
      aria-controls="menu-appbar"
      aria-haspopup="true"
      onClick={handleSidebar}
      color="inherit"
    >
      <MenuIcon />
    </IconButton>
  );

  return (
    <AppBar
      position="fixed"
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      data-testid="navbar-component"
    >
      <Container maxWidth={false}>
        <Toolbar
          disableGutters
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Banner />
          {/* xs viewport hamburger button, hidden when full logo is visible */}
          <Box sx={{ flex: { xs: '1 1 0px' }, display: { sm: 'none' } }}>
            <HamburgerButton />
          </Box>
          {/* logo and sm viewport hamburger button */}
          <Box
            sx={{
              flex: { xs: 0, sm: '0 0 auto' },
              display: 'flex',
              alignItems: 'center',
              gap: 0,
            }}
          >
            <AppLogo />
            <Box
              sx={{
                flex: { sm: '1 1 0px' },
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              <HamburgerButton />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flex: '1 1 0px',
              justifyContent: 'right',
            }}
          >
            <IconButton
              title={t('LABEL_OPEN_SETTINGS')}
              onClick={handleOpenUserMenu}
              sx={{ p: 0 }}
            >
              <Avatar>{user.username.split('')[0]}</Avatar>
            </IconButton>
            <UserMenu
              anchorElUser={anchorElUser}
              handleCloseUserMenu={handleCloseUserMenu}
              handleOpenCompanySwitch={handleOpenModal}
              setIsCreateProjectModalOpen={setIsCreateProjectModalOpen}
              signOut={signOut}
            />
          </Box>
        </Toolbar>
        <CreateProjectPartial
          isOpen={isCreateProjectModalOpen}
          onClose={closeCreateProjectModal}
        />
        <SwitchCompanyDialog
          isOpen={isOpen}
          handleCloseModal={handleCloseModal}
        />
      </Container>
    </AppBar>
  );
};

export default Navbar;
